// https://boringavatars.com/
import { svg } from "lit";
import { getContrastColor, HexColor } from "shared";

type ColorTuple = HexColor[];
const VIEW_BOX_SIZE = 38;

export interface SmileyAvatarProps {
	name: string;
	size: number | string;
	square: boolean;
	primaryColor?: string;
	colors: ColorTuple;
	contrastColors?: ColorTuple;
}

/**
 * Returns a hashcode based on a name.
 * @param name
 */
function hashCode(name: string) {
	let hash = 0;
	for (let i = 0; i < name.length; i++) {
		const character = name.charCodeAt(i);
		hash = (hash << 5) - hash + character;
		hash = hash & hash; // Convert to 32bit integer
	}
	return Math.abs(hash);
}

/**
 * Rounds a digit.
 * @param number
 * @param ntn
 */
function roundDigit(number: number, ntn: number) {
	return Math.floor((number / Math.pow(10, ntn)) % 10);
}

/**
 * Returns a boolean based on a two numbers.
 * @param num
 * @param ntn
 */
function getBoolean(num: number, ntn: number) {
	return !(roundDigit(num, ntn) % 2);
}

/**
 * Returns a unit based on some numbers.
 * @param num
 * @param range
 * @param index
 */
function getUnit(num: number, range: number, index?: number) {
	let value = num % range;

	if (index && roundDigit(num, index) % 2 === 0) {
		return -value;
	} else return value;
}

/**
 * Returns a random color.
 * @param num
 * @param colors
 * @param range
 */
function getRandomColor(num: number, colors: ColorTuple, range: number): string {
	return colors[num % range];
}

/**
 * Generates data for creating the avatar.
 * @param name
 * @param colors
 * @param contrastColors
 * @param primaryColor
 * @param size
 */
export function generateAvatarData(
	name: string,
	colors: ColorTuple,
	contrastColors?: ColorTuple,
	primaryColor?: string,
	size: number = VIEW_BOX_SIZE
) {
	const numFromName = hashCode(name);
	const range = colors && colors.length;
	const wrapperColor = primaryColor ?? getRandomColor(numFromName, colors, range);
	const wrapperContrastColor =
		contrastColors != null ? getRandomColor(numFromName, contrastColors, range) : getContrastColor(wrapperColor);
	const preTranslateX = getUnit(numFromName, 10, 1);
	const wrapperTranslateX = preTranslateX < 5 ? preTranslateX + size / 9 : preTranslateX;
	const preTranslateY = getUnit(numFromName, 10, 2);
	const wrapperTranslateY = preTranslateY < 5 ? preTranslateY + size / 9 : preTranslateY;

	return {
		wrapperColor: wrapperColor,
		faceColor: wrapperContrastColor,
		backgroundColor: wrapperColor, //getRandomColor(numFromName + 13, colors, range),
		wrapperTranslateX: wrapperTranslateX,
		wrapperTranslateY: wrapperTranslateY,
		wrapperRotate: getUnit(numFromName, 360),
		wrapperScale: 1 + getUnit(numFromName, size / 12) / 10,
		isMouthOpen: getBoolean(numFromName, 2),
		isCircle: getBoolean(numFromName, 1),
		eyeSpread: getUnit(numFromName, 5),
		mouthSpread: getUnit(numFromName, 3),
		faceRotate: getUnit(numFromName, 10, 3),
		faceTranslateX: wrapperTranslateX > size / 6 ? wrapperTranslateX / 2 : getUnit(numFromName, 8, 1),
		faceTranslateY: wrapperTranslateY > size / 6 ? wrapperTranslateY / 2 : getUnit(numFromName, 7, 2)
	};
}

/**
 * Renders a smiley avatar.
 * @param props
 */
export const renderSmileyAvatar = (props: SmileyAvatarProps) => {
	const size = VIEW_BOX_SIZE;
	const data = generateAvatarData(props.name, props.colors, props.contrastColors, props.primaryColor, size);

	return svg`
		<svg
			viewBox="0 0 ${size} ${size}"
			fill="none"
			role="img"
			xmlns="http://www.w3.org/2000/svg"
			width="${props.size}"
			height="${props.size}"
		>
			<mask id="mask__beam" maskUnits="userSpaceOnUse" x="${0}" y="${0}" width="${size}" height="${size}">
				<rect width="${size}" height="${size}" rx="${props.square ? undefined : size * 2}" fill="#FFFFFF" />
			</mask>
			<g mask="url(#mask__beam)">
				<rect width="${size}" height="${size}" fill="${data.backgroundColor}" />
				<rect
					x="0"
					y="0"
					width="${size}"
					height="${size}"
					transform="${`translate(${data.wrapperTranslateX} ${data.wrapperTranslateY}) rotate(${data.wrapperRotate} ${size / 2} ${
						size / 2
					}) scale(${data.wrapperScale})`}"
					fill="${data.wrapperColor}"
					rx="${data.isCircle ? size : size / 6}"
				/>
				<g
					transform="${`translate(${data.faceTranslateX} ${data.faceTranslateY}) rotate(${data.faceRotate} ${size / 2} ${
						size / 2
					})`}"
				>
					${
						data.isMouthOpen
							? svg`
								<path
									id="mouth"
									d="${`M15,${19 + data.mouthSpread}c2 2 4 1 6 0`}"
									stroke-width="2"
									stroke="${data.faceColor}"
									fill="none"
									stroke-linecap="round"
								/>
						  `
							: svg`
								<path
									id="mouth"
									d="${`M13,${19 + data.mouthSpread} a1,0.85 0 0,0 10,0`}"
									fill="${data.faceColor}"
								/>
						  `
					}
					<rect
						id="eye-left"
						x="${14 - data.eyeSpread}"
						y="${13}"
						width="${3.5}"
						height="${4.5}"
						rx="${1}"
						stroke="none"
						fill="${data.faceColor}"
					/>
					<rect
						id="eye-right"
						x="${20 + data.eyeSpread}"
						y="${13}"
						width="${3.5}"
						height="${4.5}"
						rx="${1}"
						stroke="none"
						fill="${data.faceColor}"
					/>
				</g>
			</g>
		</svg>
	`;
};
